// ─────────────────────────────────────────────────────────────────────────────
// LISTINO SAGRA DI ROVERETO
//
// ⚠️  Gli `id` qui sotto sono SEGNAPOSTO (1, 2, 3…).
// Devono corrispondere agli ID articolo del database del software
// "Gestione Sagra" installato in cassa (gli stessi che il server web
// integrato espone via /rest/articoli_per_tipologia/<id>).
// Prima della sagra: esportare il listino dal software e sostituire gli id.
// ─────────────────────────────────────────────────────────────────────────────

export type MenuModifier = {
  // ⚠️ `ingredientId` è l'ID della riga articolo-ingrediente nel database di
  // Gestione Sagra (tabella di collegamento, NON l'id dell'ingrediente).
  // Va esportato dal loro programma insieme agli ID articolo.
  ingredientId: number
  label: string
}

export type MenuItem = {
  id: number
  name: string
  detail?: string
  price: number
  /** se true, i modificatori devono coprire TUTTA la quantità ordinata
   *  (es. ogni caffè corretto deve avere la sua correzione) */
  modRequired?: boolean
  /** testo dell'avviso quando i modificatori obbligatori non bastano */
  modPrompt?: string
  /** come il modificatore si combina col nome nel riepilogo:
   *  - 'con' (default): "Caffè corretto" + "con Grappa"
   *  - 'variant': il label È il prodotto e sostituisce il nome ("Amaro del Capo")
   *  - 'attr': il label è un attributo in minuscolo ("Acqua 0,5 L" + "naturale")
   *  - 'exclusion': il label è un'esclusione in minuscolo ("Bruschetta zola" + "senza aglio") */
  modDisplay?: 'con' | 'variant' | 'attr' | 'exclusion'
  modifiers?: MenuModifier[]
}

export type MenuCategory = {
  key: string
  label: string
  note?: string
  items: MenuItem[]
}

export const MENU: MenuCategory[] = [
  {
    key: 'bruschette',
    label: 'Bruschette',
    items: [
      { id: 1, name: 'Bruschetta pizzaiola', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9001, label: 'Senza aglio' }], price: 4.0 },
      { id: 2, name: 'Bruschetta cotto', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9001, label: 'Senza aglio' }], price: 4.0 },
      { id: 3, name: 'Bruschetta zola', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9001, label: 'Senza aglio' }], price: 4.0 },
      { id: 4, name: 'Bruschetta farcita', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9001, label: 'Senza aglio' }], detail: 'pizzaiola con cotto', price: 4.0 },
    ],
  },
  {
    key: 'cucina',
    label: 'Cucina',
    note: 'Sabato, domenica e lunedì',
    items: [
      { id: 5, name: 'Tortelli cremaschi', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9002, label: 'Senza lattosio' }], detail: 'solo sabato e domenica', price: 9.0 },
      { id: 6, name: 'Pasta al ragù', price: 6.0 },
      { id: 7, name: 'Pasta bianca', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9002, label: 'Senza lattosio' }], price: 3.5 },
      { id: 39, name: 'Pasta al pesto', modDisplay: 'exclusion', modifiers: [{ ingredientId: 9002, label: 'Senza lattosio' }], price: 3.5 },
      { id: 8, name: 'Trippa', price: 8.0 },
    ],
  },
  {
    key: 'rosticceria',
    label: 'Rosticceria',
    items: [
      { id: 9, name: 'Arrosticini', detail: 'specialità del venerdì · a pezzo', price: 1.0 },
      { id: 10, name: 'Grigliata mista', detail: 'salamella, spiedino e coppa', price: 9.0 },
      { id: 11, name: 'Piatto di spiedini', detail: 'n. 2', price: 5.0 },
      { id: 12, name: 'Panino con la salamella', price: 4.0 },
      { id: 13, name: 'Panino con la porchetta di Ariccia', price: 5.0 },
      { id: 14, name: 'Fritto calamari', price: 11.0 },
      { id: 15, name: 'Patatine', price: 3.0 },
      { id: 16, name: 'Pesciolini fritti', detail: 'specialità del lunedì', price: 7.0 },
    ],
  },
  {
    key: 'bevande',
    label: 'Bevande',
    items: [
      { id: 17, name: 'Acqua 0,5 L', modRequired: true, modPrompt: 'Scegli il tipo', modDisplay: 'attr', modifiers: [{ ingredientId: 9012, label: 'Naturale' }, { ingredientId: 9013, label: 'Frizzante' }], price: 1.0 },
      { id: 19, name: 'Lattina', modRequired: true, modPrompt: 'Scegli la lattina', modDisplay: 'variant', modifiers: [{ ingredientId: 9014, label: 'Coca Cola' }, { ingredientId: 9015, label: 'Fanta' }, { ingredientId: 9016, label: 'Sprite' }, { ingredientId: 9017, label: 'Thè limone' }, { ingredientId: 9018, label: 'Thè pesca' }], price: 2.5 },
      { id: 21, name: 'Birra spina 0,4 L', modRequired: true, modPrompt: 'Scegli la birra', modDisplay: 'attr', modifiers: [{ ingredientId: 9019, label: 'Bionda' }, { ingredientId: 9020, label: 'Ambrata' }], price: 4.5 },
    ],
  },
  {
    key: 'vino',
    label: 'Vino',
    items: [
      { id: 23, name: 'Bott. rosso fermo', detail: 'Gutturnio Superiore', price: 10.0 },
      { id: 24, name: 'Bott. rosso frizzante', detail: 'Gutturnio', price: 9.0 },
      { id: 25, name: 'Bott. bianco fermo', detail: 'Ortrugo Contemplio', price: 10.0 },
      { id: 26, name: 'Bott. bianco frizzante', detail: 'Ortrugo', price: 9.0 },
      { id: 27, name: 'Bott. prosecco', detail: 'PerlAnima Millesimato', price: 11.0 },
      { id: 28, name: 'Bicchiere di vino', modRequired: true, modPrompt: 'Scegli il vino', modDisplay: 'attr', modifiers: [{ ingredientId: 9021, label: 'Rosso fermo' }, { ingredientId: 9022, label: 'Rosso frizzante' }, { ingredientId: 9023, label: 'Bianco fermo' }, { ingredientId: 9024, label: 'Bianco frizzante' }], price: 1.5 },
    ],
  },
  {
    key: 'conclusione',
    label: 'Conclusione',
    items: [
      { id: 32, name: 'Caffè', price: 1.0 },
      { id: 33, name: 'Caffè corretto', modRequired: true, modifiers: [{ ingredientId: 9003, label: 'Grappa' }, { ingredientId: 9004, label: 'Sambuca' }, { ingredientId: 9005, label: 'Cognac' }], price: 1.5 },
      { id: 34, name: 'Amaro', modRequired: true, modPrompt: "Scegli l'amaro", modDisplay: 'variant', modifiers: [{ ingredientId: 9006, label: 'Grappa' }, { ingredientId: 9007, label: 'Sambuca' }, { ingredientId: 9008, label: 'Liquirizia' }, { ingredientId: 9009, label: 'Limoncello' }, { ingredientId: 9010, label: 'Amaro del Capo' }, { ingredientId: 9011, label: 'Montenegro' }], price: 3.0 },
      { id: 35, name: 'Fetta di torta', price: 3.0 },
      { id: 36, name: 'Macedonia', price: 3.0 },
    ],
  },
]

export const ALL_ITEMS: MenuItem[] = MENU.flatMap((c) => c.items)

export const ITEM_BY_ID = new Map(ALL_ITEMS.map((i) => [i.id, i]))

export function formatEuro(n: number): string {
  return n.toFixed(2).replace('.', ',') + ' €'
}
